import { useState } from 'react'
import { Snowflake, AlertTriangle, Clock, CheckCircle, Users, Layers, Loader2 } from 'lucide-react'
import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { fadeInUp, staggerContainer, cardReveal, tapScale } from '@/lib/motion'
import { Drawer } from 'vaul'
import { useToast } from '@/components/shared/Toast'
import { HandTimeline } from '@/components/admin/HandTimeline'
import type { Street } from '@/components/admin/HandTimeline'

interface FrozenPot {
  id: string; handId: string; room: string; stakes: string; pot: number
  frozenAt: string; status: 'pending' | 'resolved'
  libraryA: { winner: string; hand: string }; libraryB: { winner: string; hand: string }
  players?: { name: string; cards: string[] | null; hand?: string; folded?: boolean }[]
  communityCards?: string[]; resolvedWinner?: string; resolvedAt?: string
  streets?: Street[]
}

const frozenPots: FrozenPot[] = [
  {
    id: 'FP001', handId: 'H-7839', room: 'D4J6', stakes: '1/2', pot: 8800, frozenAt: '14:15', status: 'pending',
    libraryA: { winner: '小明', hand: '同花順' }, libraryB: { winner: 'Lucky88', hand: '同花' },
    players: [
      { name: '小明', cards: ['A\u2660', 'K\u2660'], hand: '同花順' },
      { name: 'Lucky88', cards: ['Q\u2665', 'J\u2665'], hand: '同花' },
      { name: '阿花', cards: null, folded: true },
    ],
    communityCards: ['10\u2660', 'J\u2660', 'Q\u2660', '2\u2663', '7\u2666'],
    streets: [
      { name: 'Preflop', communityCards: [], actions: [
        { player: '小明', action: 'raise', amount: '3BB' },
        { player: 'Lucky88', action: 'call' },
        { player: '阿花', action: 'call' },
      ]},
      { name: 'Flop', communityCards: [{ rank: '10', suit: 'spade' }, { rank: 'J', suit: 'spade' }, { rank: 'Q', suit: 'spade' }], actions: [
        { player: '小明', action: 'bet', amount: '6BB' },
        { player: 'Lucky88', action: 'raise', amount: '18BB' },
        { player: '阿花', action: 'fold' },
        { player: '小明', action: 'call' },
      ]},
      { name: 'Turn', communityCards: [{ rank: '2', suit: 'club' }], actions: [
        { player: '小明', action: 'check' },
        { player: 'Lucky88', action: 'bet', amount: '25BB' },
        { player: '小明', action: 'call' },
      ]},
      { name: 'River', communityCards: [{ rank: '7', suit: 'diamond' }], actions: [
        { player: '小明', action: 'all-in' },
        { player: 'Lucky88', action: 'call' },
      ]},
    ],
  },
  {
    id: 'FP002', handId: 'H-7701', room: 'A1F3', stakes: '2/5', pot: 14600, frozenAt: '昨天 22:45', status: 'resolved',
    libraryA: { winner: 'PokerKing', hand: '順子' }, libraryB: { winner: 'Sarah_HK', hand: '兩對' },
    players: [
      { name: 'PokerKing', cards: ['5\u2665', '6\u2660'], hand: '順子' },
      { name: 'Sarah_HK', cards: ['K\u2665', '9\u2665'], hand: '兩對' },
      { name: '大衛王', cards: null, folded: true },
    ],
    communityCards: ['3\u2666', '4\u2663', '7\u2660', 'K\u2663', '9\u2660'],
    resolvedWinner: 'PokerKing', resolvedAt: '昨天 23:10',
    streets: [
      { name: 'Preflop', communityCards: [], actions: [
        { player: '大衛王', action: 'raise', amount: '3BB' },
        { player: 'PokerKing', action: 'call' },
        { player: 'Sarah_HK', action: 'call' },
      ]},
      { name: 'Flop', communityCards: [{ rank: '3', suit: 'diamond' }, { rank: '4', suit: 'club' }, { rank: '7', suit: 'spade' }], actions: [
        { player: '大衛王', action: 'bet', amount: '6BB' },
        { player: 'PokerKing', action: 'call' },
        { player: 'Sarah_HK', action: 'call' },
      ]},
      { name: 'Turn', communityCards: [{ rank: 'K', suit: 'club' }], actions: [
        { player: '大衛王', action: 'check' },
        { player: 'PokerKing', action: 'bet', amount: '12BB' },
        { player: 'Sarah_HK', action: 'raise', amount: '30BB' },
        { player: '大衛王', action: 'fold' },
        { player: 'PokerKing', action: 'call' },
      ]},
      { name: 'River', communityCards: [{ rank: '9', suit: 'spade' }], actions: [
        { player: 'PokerKing', action: 'all-in' },
        { player: 'Sarah_HK', action: 'call' },
      ]},
    ],
  },
]

const suitColor = (card: string) => {
  if (card.includes('\u2665') || card.includes('\u2666')) return 'text-rose-400 bg-rose-500/10 border-rose-500/20'
  return 'text-sky-400 bg-sky-500/10 border-sky-500/20'
}

export function AdminFrozenPotsScreen() {
  const { toast } = useToast()
  const [potList, setPotList] = useState<FrozenPot[]>(frozenPots)
  const [selectedPot, setSelectedPot] = useState<FrozenPot | null>(null)
  const [selectedWinner, setSelectedWinner] = useState('')
  const [splitEnabled, setSplitEnabled] = useState(false)
  const [splitAmounts, setSplitAmounts] = useState<Record<string, string>>({})
  const [splitError, setSplitError] = useState('')
  const [settling, setSettling] = useState(false)
  const pendingCount = potList.filter((p) => p.status === 'pending').length

  const resetDrawer = () => { setSelectedPot(null); setSelectedWinner(''); setSplitEnabled(false); setSplitAmounts({}); setSplitError(''); setSettling(false) }
  const activePlayers = selectedPot?.players?.filter((p) => !p.folded) ?? []

  const handleConfirmSettle = () => {
    if (!selectedPot) return

    // Validate split amounts if split is enabled
    if (splitEnabled) {
      const totalCents = selectedPot.pot
      const totalDisplay = totalCents / 100
      const sum = activePlayers.reduce((acc, p) => {
        const val = parseFloat(splitAmounts[p.name] || '0')
        return acc + val
      }, 0)
      // Round to 2 decimal places to avoid floating point issues
      const roundedSum = Math.round(sum * 100) / 100
      if (roundedSum !== totalDisplay) {
        setSplitError(`分配金額總計 $${roundedSum.toFixed(2)} 不等於彩池總額 $${totalDisplay.toFixed(2)}`)
        return
      }
      setSplitError('')
    }

    setSettling(true)
    setTimeout(() => {
      const resolvedWinner = splitEnabled ? '平分' : selectedWinner
      setPotList((prev) => prev.map((p) =>
        p.id === selectedPot.id
          ? { ...p, status: 'resolved' as const, resolvedWinner, resolvedAt: new Date().toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' }) }
          : p
      ))
      toast({ type: 'success', title: '彩池已結算' })
      resetDrawer()
    }, 500)
  }

  return (
    <motion.div variants={staggerContainer} initial="hidden" animate="visible" className="flex flex-col h-full">
      {/* Header Banner */}
      <motion.div variants={fadeInUp} className="px-4 pt-4 pb-2">
        <div className="p-3 rounded-2xl glass-card border border-amber-500/20 bg-amber-500/5 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-xs text-amber-200/90 leading-relaxed">當兩個評估引擎結果不一致時，彩池將被凍結，需要人工審核。</p>
            <p className="text-xs text-amber-400 font-bold mt-1.5">目前 {pendingCount} 個凍結彩池待處理</p>
          </div>
        </div>
      </motion.div>

      {/* Frozen Pots List */}
      <motion.main variants={staggerContainer} className="flex-1 overflow-y-auto px-4 pb-24 scrollbar-hide space-y-3 pt-2">
        {potList.map((pot) => (
          <motion.div
            key={pot.id} variants={cardReveal}
            onClick={() => { setSelectedPot(pot); setSelectedWinner('') }}
            className="p-4 rounded-2xl glass-card space-y-3 active:scale-[0.98] transition-transform cursor-pointer"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={cn('w-10 h-10 rounded-full flex items-center justify-center', pot.status === 'pending' ? 'bg-amber-500/10' : 'bg-emerald-500/10')}>
                  <Snowflake className={cn('w-5 h-5', pot.status === 'pending' ? 'text-amber-400' : 'text-emerald-400')} />
                </div>
                <div>
                  <p className="text-sm font-bold text-foreground">牌局 {pot.handId}</p>
                  <p className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" /> {pot.frozenAt}</p>
                </div>
              </div>
              <p className="text-xl font-bold font-mono text-gold">${(pot.pot / 100).toFixed(2)}</p>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-mono text-muted-foreground px-2 py-0.5 rounded-lg bg-surface-elevated">房間 {pot.room}</span>
                <span className="text-[10px] font-mono text-muted-foreground px-2 py-0.5 rounded-lg bg-surface-elevated">${pot.stakes}</span>
              </div>
              <span className={cn(
                'text-[10px] font-bold px-2.5 py-0.5 rounded-full border',
                pot.status === 'pending' ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
              )}>
                {pot.status === 'pending' ? '待處理' : '已解決'}
              </span>
            </div>
            {pot.status === 'resolved' && pot.resolvedWinner && (
              <div className="flex items-center gap-2 text-xs text-emerald-400/80">
                <CheckCircle className="w-3.5 h-3.5" />
                <span>已結算給 {pot.resolvedWinner} -- {pot.resolvedAt}</span>
              </div>
            )}
          </motion.div>
        ))}
      </motion.main>

      {/* Resolution Drawer */}
      <Drawer.Root open={selectedPot !== null} onOpenChange={(open) => { if (!open) resetDrawer() }}>
        <Drawer.Portal>
          <Drawer.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" />
          <Drawer.Content className="fixed bottom-0 left-0 right-0 z-50 outline-none">
            <div className="bg-surface-elevated rounded-t-3xl border-t border-foreground/10 max-h-[85vh] overflow-y-auto">
              <div className="flex justify-center py-3"><div className="w-10 h-1.5 rounded-full bg-foreground/20" /></div>
              <div className="px-6 pb-8 safe-area-bottom space-y-5">
                {/* Hand Info Header */}
                <div>
                  <Drawer.Title className="font-display text-2xl font-semibold text-foreground">牌局 {selectedPot?.handId}</Drawer.Title>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    房間 {selectedPot?.room} -- ${selectedPot?.stakes} -- 彩池{' '}
                    <span className="font-mono text-gold">${selectedPot ? (selectedPot.pot / 100).toFixed(2) : '0.00'}</span>
                  </p>
                </div>

                {/* Disagreement Section */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-4 h-4 text-rose-400" />
                    <span className="text-xs font-bold text-rose-400 uppercase tracking-wider">引擎分歧</span>
                  </div>
                  <div className="p-3 rounded-xl bg-surface-elevated border border-foreground/10 space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-muted-foreground">Library A (pokersolver)</span>
                      <span className="text-xs font-bold text-foreground">Winner: {selectedPot?.libraryA.winner} ({selectedPot?.libraryA.hand})</span>
                    </div>
                    <div className="h-px bg-foreground/5" />
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-muted-foreground">Library B (poker-evaluator)</span>
                      <span className="text-xs font-bold text-foreground">Winner: {selectedPot?.libraryB.winner} ({selectedPot?.libraryB.hand})</span>
                    </div>
                  </div>
                  {selectedPot && selectedPot.libraryA.winner !== selectedPot.libraryB.winner && (
                    <p className="text-[10px] text-rose-400 font-medium px-1">贏家判定不同 -- 需要人工確認正確結果</p>
                  )}
                  {selectedPot && selectedPot.libraryA.winner === selectedPot.libraryB.winner && (
                    <p className="text-[10px] text-amber-400 font-medium px-1">牌型分類不同 -- {selectedPot.libraryA.hand} vs {selectedPot.libraryB.hand}</p>
                  )}
                </div>

                {/* Community Cards */}
                {selectedPot?.communityCards && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Layers className="w-3.5 h-3.5 text-muted-foreground" />
                      <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">公共牌</span>
                    </div>
                    <div className="flex gap-2">
                      {selectedPot.communityCards.map((card, i) => (
                        <span key={i} className={cn('px-2.5 py-1.5 rounded-lg text-xs font-bold font-mono border', suitColor(card))}>{card}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Player Hands */}
                {selectedPot?.players && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Users className="w-3.5 h-3.5 text-muted-foreground" />
                      <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">玩家手牌</span>
                    </div>
                    <div className="space-y-2">
                      {selectedPot.players.map((player) => (
                        <div key={player.name} className={cn('p-3 rounded-xl bg-surface-elevated border', player.folded ? 'border-foreground/5 opacity-50' : 'border-foreground/10')}>
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-bold text-foreground">{player.name}</span>
                            {player.folded
                              ? <span className="text-[10px] text-muted-foreground font-medium px-2 py-0.5 rounded-full bg-foreground/5">已棄牌</span>
                              : <span className="text-[10px] font-bold text-gold px-2 py-0.5 rounded-full bg-gold/10 border border-gold/20">{player.hand}</span>}
                          </div>
                          {player.cards && (
                            <div className="flex gap-1.5 mt-2">
                              {player.cards.map((card, i) => (
                                <span key={i} className={cn('px-2 py-1 rounded-md text-xs font-bold font-mono border', suitColor(card))}>{card}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Hand Timeline */}
                {selectedPot?.streets && (
                  <HandTimeline streets={selectedPot.streets} />
                )}

                {/* Resolution Actions (pending only) */}
                {selectedPot?.status === 'pending' && selectedPot.players && (
                  <div className="space-y-4 pt-2">
                    <div className="h-px bg-foreground/10" />
                    <div className="space-y-2">
                      <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">選擇贏家</span>
                      <div className="space-y-1.5">
                        {activePlayers.map((player) => (
                          <label key={player.name} className={cn(
                            'flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors',
                            selectedWinner === player.name && !splitEnabled ? 'bg-gold/10 border-gold/30' : 'bg-surface-elevated border-foreground/10'
                          )}>
                            <input type="radio" name="winner" value={player.name}
                              checked={selectedWinner === player.name && !splitEnabled}
                              onChange={() => { setSelectedWinner(player.name); setSplitEnabled(false) }}
                              className="accent-gold w-4 h-4" />
                            <span className="text-sm font-bold text-foreground">{player.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Split Pot Toggle */}
                    <div className="flex items-center justify-between p-3 rounded-xl bg-surface-elevated border border-foreground/10">
                      <span className="text-sm text-foreground font-medium">平分彩池</span>
                      <button onClick={() => { setSplitEnabled(!splitEnabled); setSelectedWinner('') }}
                        className={cn('w-11 h-6 rounded-full transition-colors relative', splitEnabled ? 'bg-gold' : 'bg-foreground/10')}>
                        <div className={cn('w-5 h-5 rounded-full bg-white absolute top-0.5 transition-all', splitEnabled ? 'left-[22px]' : 'left-0.5')} />
                      </button>
                    </div>

                    {splitEnabled && (
                      <div className="space-y-2">
                        {activePlayers.map((player) => (
                          <div key={player.name} className="flex items-center gap-3 p-3 rounded-xl bg-surface-elevated border border-foreground/10">
                            <span className="text-sm text-foreground font-medium flex-1">{player.name}</span>
                            <div className="flex items-center gap-1">
                              <span className="text-sm text-muted-foreground">$</span>
                              <input type="number" value={splitAmounts[player.name] || ''}
                                placeholder={(selectedPot.pot / 100 / activePlayers.length).toFixed(2)}
                                onChange={(e) => { setSplitAmounts({ ...splitAmounts, [player.name]: e.target.value }); setSplitError('') }}
                                className="w-24 px-2 py-1 rounded-lg bg-foreground/10 border border-foreground/10 text-sm font-mono text-foreground text-right focus:outline-none focus:border-gold/40" />
                            </div>
                          </div>
                        ))}
                        {splitError && (
                          <div className="flex items-center gap-2 p-2 rounded-lg bg-rose-500/10 border border-rose-500/20">
                            <AlertTriangle className="w-3.5 h-3.5 text-rose-400 flex-shrink-0" />
                            <p className="text-[11px] text-rose-400">{splitError}</p>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-3 pt-1">
                      <button onClick={resetDrawer} disabled={settling} className="h-14 glass-card rounded-2xl font-semibold text-foreground">取消</button>
                      <motion.button whileTap={tapScale} onClick={handleConfirmSettle} disabled={settling || (!splitEnabled && !selectedWinner)}
                        className={cn('h-14 rounded-2xl font-bold text-white transition-opacity flex items-center justify-center gap-2', settling || (!splitEnabled && !selectedWinner) ? 'bg-gold/30 opacity-50' : 'bg-gold')}>
                        {settling ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            處理中...
                          </>
                        ) : '確認結算'}
                      </motion.button>
                    </div>
                  </div>
                )}

                {/* Resolved state info */}
                {selectedPot?.status === 'resolved' && (
                  <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20 space-y-1">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                      <span className="text-sm font-bold text-emerald-400">已結算</span>
                    </div>
                    <p className="text-xs text-emerald-400/70">贏家: {selectedPot.resolvedWinner} -- 結算時間: {selectedPot.resolvedAt}</p>
                  </div>
                )}
              </div>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </motion.div>
  )
}
